<template>
  <article
    ref="productItem"
    class="product-item js-product-item"
    :data-product-id="product.id"
    :data-is-child-of-parent="parentProduct.handle || null"
  >
    <div
      @mouseenter="handleMouseEnter($event)"
      @mouseleave="handleMouseLeave($event)"
    >
      <ProductItemGallery
        :activeColor="activeColor"
        :alt="alt"
        :badge="badge"
        :canPrintArtwork="canPrintArtwork"
        :customerUploadActive="customerUploadActive"
        :errorUploadMessage="errorUploadMessage"
        :frameSpec="frameSpec"
        :frameSpecUrl="frameSpecUrl"
        :hasMaskedRendering="showMaskRenderer"
        :isBundle="isBundle"
        :isCollectionPage="isCollectionPage"
        :isQuickview="isQuickview"
        :isUploadProcessing="isUploadProcessing"
        :productImages="productImages"
        :src="src"
        :type="type"
        :url="url"
        :isHovered="isHovered"
        :hoveredImage="hoveredImage"
        :isLoading="isLoading"
        :disableCarousel="disableProductItemGalleryCarousel"
        :onImageHover="onImageHover"
        :parentProduct="parentProduct"
        :parentProductVariant="parentProductVariant"
      />
    </div>
    <div
      :class="{
        'pi__desc-bottom--gift-card': isGiftCardPage,
        'is-loading-upload': isUploadProcessing
      }"
      class="pi__desc"
    >
      <div
        v-if="valueProps"
        class="pi__value-props"
      >
        <svg
          v-if="product.type === 'Custom Frame'"
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          class="pi__value-props-icon"
        >
          <path d="M3.46719 9.6C3.31719 9.4125 3.16719 9.075 3.09219 8.85L2.82969 7.875L2.56719 8.85C2.49219 9.15 2.34219 9.4125 2.19219 9.6C2.04219 9.7875 1.81719 10.0125 1.59219 10.125L0.804688 10.5L1.59219 10.875C1.81719 10.9875 2.00469 11.1375 2.19219 11.4C2.34219 11.5875 2.49219 11.925 2.56719 12.15L2.82969 13.125L3.09219 12.15C3.16719 11.85 3.31719 11.5875 3.46719 11.4C3.61719 11.2125 3.84219 10.9875 4.06719 10.875L4.85469 10.5L4.06719 10.125C3.84219 10.0125 3.65469 9.8625 3.46719 9.6Z" fill="#1D2019" stroke="#1D2019" stroke-width="0.75" stroke-linejoin="round"/>
          <path d="M3.09219 4.64976C3.16719 4.34976 3.31719 4.08726 3.46719 3.89976C3.61719 3.71226 3.84219 3.48726 4.06719 3.37476L4.85469 2.99976L4.06719 2.62476C3.84219 2.51226 3.65469 2.36226 3.46719 2.09976C3.31719 1.91226 3.16719 1.57476 3.09219 1.34976L2.82969 0.374756L2.56719 1.34976C2.49219 1.64976 2.34219 1.91226 2.19219 2.09976C2.04219 2.28726 1.81719 2.51226 1.59219 2.62476L0.804688 2.99976L1.59219 3.37476C1.81719 3.48726 2.00469 3.63726 2.19219 3.89976C2.34219 4.08726 2.49219 4.42476 2.56719 4.64976L2.82969 5.62476L3.09219 4.64976Z" fill="#1D2019" stroke="#1D2019" stroke-width="0.75" stroke-linejoin="round"/>
          <path d="M8.62291 7.31801L6.74023 9.20068L12.2542 14.7146L14.1368 12.8319L8.62291 7.31801Z" fill="#1D2019" stroke="#1D2019" stroke-width="0.75" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6.61705 5.32143L4.73438 7.2041L6.77615 9.24587L8.65882 7.3632L6.61705 5.32143Z" stroke="#1D2019" stroke-width="0.75" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.2133 4.64976C10.2883 4.34976 10.4383 4.08726 10.5883 3.89976C10.7383 3.71226 10.9633 3.48726 11.1883 3.37476L11.9758 2.99976L11.1883 2.62476C10.9633 2.51226 10.7758 2.36226 10.5883 2.09976C10.4383 1.91226 10.2883 1.57476 10.2133 1.34976L9.95078 0.374756L9.68828 1.34976C9.61328 1.64976 9.46328 1.91226 9.31328 2.09976C9.16328 2.28726 8.93828 2.51226 8.71328 2.62476L7.92578 2.99976L8.71328 3.37476C8.93828 3.48726 9.12578 3.63726 9.31328 3.89976C9.46328 4.08726 9.61328 4.42476 9.68828 4.64976L9.95078 5.62476L10.2133 4.64976Z" fill="#1D2019" stroke="#1D2019" stroke-width="0.75" stroke-linejoin="round"/>
        </svg>
        {{ valueProps }}
      </div>
      <div v-if="collectionLabel" class="pi__collection-label">
        <a :href="collectionLabel.url">{{ collectionLabel.copy }}</a>
      </div>
      <h3 class="pi__title">
        <a
          v-if="!isQuickview"
          :href="productUrl"
          :title="productUrl"
          :class="['pi__title-link', {'hide' : isGiftCardPage}]"
        >
          <span v-html="processedTitle"></span>
        </a>
        <span
          v-else
          :class="{'hide' : isGiftCardPage}"
        >
          <span v-html="processedTitle"></span>
        </span>
      </h3>
      <div
        v-if="isGiftCardPage"
        class="pi__desc-bottom"
      >
        <p
          v-if="type !== 'Retail Events'"
          class="pi__price pi__price--large"
        >
          <span v-if="hasVariedPrices">Starting at</span>
          {{ formattedPrice(price) }}
        </p>
        <p
          v-if="giftCardTitle"
          class="pi__subtitle pi__subtitle--slim"
        >
          {{ giftCardTitle }}
        </p>
      </div>
      <div
        v-else
        class="pi__desc-bottom"
      >
        <div
          class="pi__subtitle js-pi-subtitle"
        >
          {{ subtitle }}
        </div>
        <div
          v-if="(subtitle && showSwatches && colors?.length > 0)"
          class="pi__subtitle pi__subtitle--one-row js-pi-subtitle"
        >
          {{ subtitle }}
        </div>
        <div
          :class="{'pi__swatches-wrapper--swatchless' : !showSwatches || colors?.length <= 1}"
          class="pi__swatches-wrapper"
        >
          <div
            v-if="showSwatches === true"
            class="pi__swatches-list"
          >
            <ProductSwatches
              v-if="colors?.length > 0"
              :activeColor="activeColor"
              :initialColor="initialColor"
              :colors="colors"
              :images="productImages"
              :updateImage="updateImage"
              :url="url"
              :noImage="noImage"
              :parent="productItem"
              :onSwatchHover="onSwatchHover"
              :handleMouseLeave="handleMouseLeave"
            />
          </div>
        </div>
        <div
          v-if="showSwatches && colors?.length > 0 && activeVariantTitle"
          class="pi__subtitle  pi__subtitle--one-row"
        >
          {{ activeVariantTitle }}
        </div>
        <div
          v-if="subtitle && !showSwatches"
          class="pi__subtitle pi__subtitle--one-row js-pi-subtitle"
        >
          {{ subtitle }}
        </div>
        <p
          v-if="type !== 'Retail Events'"
          class="pi__price"
        >
          <span v-if="hasVariedPrices">Starting at</span>
          {{ formattedPrice(price) }}
        </p>
      </div>
    </div>
  </article>
</template>

<script>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRefs,
  watch
} from "vue";

import carousel from "../../lib/carousel";
import useFrameSpecData from "../composables/frame-spec";
import useMediaQuery from "../composables/use-media-query";
import ProductItemGallery from "./ProductItemGallery";
import ProductSwatches from "./ProductSwatches";

export default {
  name: "ProductItem",
  components: {
    ProductSwatches,
    ProductItemGallery
  },
  props: {
    handle: String,
    product: Object,
    featuredDefaultImage: Object,
    images: Array,
    isQuickview: Boolean,
    swatches: Array,
    productSubtitle: String,
    giftCardTitle: String,
    productBadge: Object,
    // Per-variant badge overrides — { [variantId]: badgeObject | null }
    variantBadges: Object,
    productDimensions: String,
    productValueProps: String,
    isCollectionPage: Boolean,
    isSearchPage: Boolean,
    altVariantImagesArray: Array,
    colorFilterValuesArray: Array,
    maskedValuesArray: Array,
    comparePanelVariantAssetsArray: Array,
    selectedVariantId: Number,
    variantSpecArray: Array,
    parentProduct: Object,
    collectionLabel: Object,
    allFullMouldings: Array,
  },
  setup(props) {
    const {
      msrp,
      price,
      title,
      handle,
      type
    } = toRefs(props.product);
    const productItem = ref(null);
    const activeColor = ref({});
    const initialColor = ref({});
    const isActive = ref(false);
    const isLoading = ref(false);
    const activeVariantId = ref(null);
    const isQuickview = ref(props.isQuickview);
    const subtitle = ref(props.productSubtitle ?? "");
    const isHovered = ref(false);
    const hoveredImage = ref("");
    const activeVariantTitle = ref(null);
    const noImage = ref(
      "//cdn.shopify.com/shopifycloud/shopify/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif"
    );
    const src = ref(props.images.length > 0
      ? props.images[0].src
      : noImage.value);
    const url = ref(`/products/${handle.value}`);

    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const isGiftCardPage = SDG?.Data?.template === "product.gift-card" || false;
    const formattedPrice = (value) => __.formatPriceDisplay(value);
    const variantId = Number(props.selectedVariantId);
    const errorUploadMessage = SDG?.Data?.errorUploadMessage || "Your image is too small to print in this frame. Please upload a larger image.";

    // Process title to convert [text] to <sup>text</sup> for proper HTML rendering
    const processedTitle = computed(() => {
      return title.value.replace(/\[/g, '<sup>').replace(/\]/g, '</sup>');
    });
    const hasMaskedRendering = computed(() => {
      const selectedObject = props.maskedValuesArray
        .find((item) => item.id === variantId);

      const isMasked = selectedObject
        && selectedObject.masked_rendering && selectedObject.masked_rendering.length > 0;
      return isMasked;
    });

    const productOrParentProduct = computed(() => {
      return Object.keys(props.parentProduct).length > 0 ? props.parentProduct : props.product;
    });

    const productImages = computed(() => (
      props.images ?? []
    ));

    const badge = computed(() => {
      // When a variant is active, check for a variant-level badge override first.
      // activeVariantId tracks the currently selected color/swatch variant.
      if (activeVariantId.value && props.variantBadges) {
        const variantBadge = props.variantBadges[String(activeVariantId.value)];
        // Only use the variant badge if it has copy — otherwise fall through to product badge
        if (variantBadge?.copy) {
          return variantBadge;
        }
      }
      // Fall back to the product-level badge
      return props.productBadge ?? null;
    });

    const dimensions = computed(() => (
      props.productDimensions ?? ""
    ));

    const valueProps = computed(() => (
      props.productValueProps ?? ""
    ));

    const totalVariants = computed(() => (
      props.product.variants.length ?? 0
    ));

    const isBundle = computed(() => (
      props.product.tags.includes("Multi-Photo Frames")
    ));

    const variants = computed(() => (
      props.product.variants.filter((variant) => variant.available) ?? []
    ));

    // Temporary hotfix: disabling carousel
    // eslint-disable-next-line no-unused-vars
    const isTableTopFrame = computed(() => (
      props.product.tags
        .map((tag) => tag.toLowerCase())
        .includes("tabletop frames")
    ));

    const isCuratedDesignFrame = computed(() => (
      type.value?.toLowerCase() === "curated design frame"
    ));

    // eslint-disable-next-line arrow-body-style
    const disableProductItemGalleryCarousel = computed(() => {
      // Temporary hotfix: disabling carousel

      // if (isTableTopFrame.value && isCuratedDesignFrame.value) {
      //   return true;
      // }

      // return false;
      return true;
    });

    const isFrameBundle = computed(() => (
      type.value?.toLowerCase() === "frame bundle"
    ));

    const isCustomFrame = computed(() => (
      type.value?.toLowerCase() === "custom frame"
    ));

    const isMutliPhotoFrame = computed(() => (
      props.product.tags
        .map((tag) => tag.toLowerCase())
        .includes("multi-photo frames")
    ));

    const showSwatches = computed(() => {
      const { options } = props.product;

      if (!options?.length > 0) {return false;}
      const colorOption = options.filter((option) => (
        option.toLowerCase() === "frame style" || option.toLowerCase() === "color"
      ));

      if (colorOption?.length > 0) {return true;}

      return false;
    });

    const productVariants = computed(() => {
      const variantDetails = props.variantSpecArray?.map((variant) => {
        const id = variant?.json?.id || null;
        if (!id) {return null;}

        const filteredImages = [...props.altVariantImagesArray].find((obj) => obj.id === id);
        const colorFilterValues = [...props.colorFilterValuesArray].find((obj) => obj.id === id);
        const comparePanelAssets = [...props.comparePanelVariantAssetsArray].find(
          (obj) => obj.id === id
        );

        const {
          option1,
          featured_image: featuredImage
        } = variant?.json || {};

        const details = {
          option1: option1.toLowerCase().split(" ").join("_"),
          featured_image: filteredImages?.images?.length
            ? filteredImages.images[0]
            : featuredImage,
          color_filter_values: colorFilterValues?.colors,
          compare_panel_assets: comparePanelAssets?.asset,
          hoverAsset: variant?.hoverAsset || null,
          title: variant?.subtitle || variant?.json?.title || null
        };
        return details;
      }).filter(Boolean);

      if (variantDetails.length > 0) {
        const variantOptions = [];
        variantDetails.forEach((value) => {
          const formattedValue = value.option1
            ? value.option1.toLowerCase().trim().replace(":", "_").replace(/ /g, "_")
            : null;

          if (formattedValue && !variantOptions.includes(formattedValue)) {
            const detail = {
              option: formattedValue,
              featured_image: value.featured_image,
              color_filter_values: value.color_filter_values,
              compare_panel_assets: value.compare_panel_assets,
              hoverAsset: value.hoverAsset,
              title: value.title
            };

            variantOptions.push(detail);
          }
        });

        return variantOptions;
      }

      return [];
    });

    const hasVariedPrices = computed(() => {
      const prices = [];

      variants.value.forEach((variant) => {
        const {
          price: variantPrice
        } = variant;

        if (!prices.includes(variantPrice)) {
          prices.push(variantPrice);
        }
      });

      if (prices.length > 1) {
        return true;
      }

      return false;
    });

    const parentProductVariant = computed(() => {
      const { variants: parentVariants } = props.parentProduct;
      const { variants } = props.product;

      if (!parentVariants?.length || !variants?.length) return null;
      
      const firstVariantTitle = variants[0]?.title;
      const matchingParentVariant = parentVariants.find(
        variant => variant.title === firstVariantTitle
      );

      return matchingParentVariant?.id || null;
    });

    const colors = computed(() => {
      const { options } = props.product;

      const colorOption = options.filter((option) => (
        option.toLowerCase() === "frame style" || option.toLowerCase() === "color"
      ));

      const colorOptions = [];
      const optionsArray = [];

      if (colorOption.length > 0) {
        const variantsOptions = productVariants.value;
        const variantSwatches = props.swatches;

        variantsOptions.forEach((variantOption) => {
          const variantSwatch = variantSwatches.find((swatch) => {
            const { color } = swatch;
            const { option } = variantOption;

            if (!color || !option) {
              return false;
            }

            if (color === option) {
              return true;
            }

            return false;
          });

          if (variantSwatch && !colorOptions.includes(variantSwatch.color)) {
            variantSwatch.featured_image = variantOption.featured_image;
            variantSwatch.compare_panel_assets = variantOption.compare_panel_assets;
            variantSwatch.hoverAsset = variantOption.hoverAsset || null;
            variantSwatch.title = variantOption.title;

            // removes orientation and size from title
            if (variantOption?.title?.includes("/")) {
              const updatedTitle = variantOption.title.split("/")[0];
              variantSwatch.title = updatedTitle.replace(/:\s*/g, ': ');
            } else {
              variantSwatch.title = variantOption.title?.replace(/:\s*/g, ': ');
            }

            if (variantSwatch.value.includes("//")) {
              variantSwatch.type = "image";
            } else {
              variantSwatch.type = "color";
            }
            // set active color based on active color filters
            const colorFilterValues = variantOption.color_filter_values;
            if (SDG?.activeColorFilters && colorFilterValues) {
              const colorFilters = colorFilterValues.map((color) => color.toLowerCase());
              SDG.activeColorFilters.some((activeColorFilter) => {
                const activeColorFilterValue = activeColorFilter.toLowerCase();
                variantSwatch.hasActiveColor = colorFilters.includes(activeColorFilterValue);
                return variantSwatch.hasActiveColor;
              });
            }
            colorOptions.push(variantSwatch.color);
            optionsArray.push(variantSwatch);
          }
        });

        return optionsArray;
      }
      return [];
    });

    const updateImage = (evt) => {
      const $swatch = evt?.currentTarget || null;
      if ($swatch === null) {return;}

      const $swatchParent = $swatch.parentElement;
      const { image, id } = $swatch.dataset;

      if (image) {
        src.value = image;
      }

      if (id) {
        activeVariantId.value = id;
        url.value = `/products/${handle.value}?variant=${id}`;

        updateSpecOnSwatchClick(activeVariantId.value);
      }

      resetSwatches();
      __.addClass($swatch, "is-selected");
      __.addClass($swatchParent, "is-selected");

      setTimeout(() => {
        const $piImages = productItem.value.querySelectorAll(".pi__image");

        $piImages.forEach(($piImage) => __.removeClass($piImage, "is-loading"));
        initializeCarousel();
        $swatch.setAttribute("aria-pressed", "true");
        activeVariantTitle.value = $swatch.dataset.title || null;
      }, 250);
    };

    const handleStaticClick = () => {
      productItem.value.click();
    };

    const initializeCarousel = () => {
      carousel({
        dom: {
          carousel: "js-product-item-carousel",
          carouselCell: "js-product-item-carousel-cell"
        },
        staticClick: handleStaticClick
      });
    };

    const resetSwatches = () => {
      const $swatches = productItem.value.querySelectorAll(".js-product-swatch");
      $swatches.forEach(($swatch) => {
        const $swatchParent = $swatch.parentElement;
        __.removeClass($swatch, "is-selected");
        __.removeClass($swatchParent, "is-selected");
        $swatch.setAttribute("aria-pressed", "false");
      });
    };

    const handleClick = () => {
      isActive.value = !isActive.value;
    };

    const resetActive = () => {
      isActive.value = false;
    };


    const activeFilteredColor = computed(() => (
      colors.value.find((color) => color.hasActiveColor) ?? null
    ));
    let alt;
    if (props.featuredDefaultImage?.alt) {
      alt = props.featuredDefaultImage.alt;
    } else if (props.images.length > 0) {
      alt = props.images[0].alt;
    } else {
      alt = 'No image available';
    }

    const activeImage = __.getSizedImageUrl(src.value, "1x1").replace("_1x1.", "_{width}x.");

    if (activeFilteredColor.value) {
      activeColor.value = activeFilteredColor.value;
      initialColor.value = activeFilteredColor.value;
      const activeFeaturedImage = activeFilteredColor.value.featured_image;
      src.value = activeFeaturedImage ? activeFeaturedImage.src : noImage.value;
      alt = activeFeaturedImage ? activeFeaturedImage.alt : "No image available";
    } else {
      // eslint-disable-next-line prefer-destructuring
      activeColor.value = colors.value[0] ?? {};
      initialColor.value = colors.value[0] ?? {};
    }

    const initialColorValue = initialColor.value.color || initialColor.value;

    const selectedVariantObject = props.variantSpecArray
      .find((variant) => variant.option1 === initialColorValue);
    const selectedVariantSpec = selectedVariantObject?.variant_spec || null;
    activeVariantId.value = selectedVariantObject?.id;
    const selectedVariantTitle = props.variantSpecArray
      .find((variant) => variant.id === activeVariantId?.value)?.simple_title || null;

    const updateTitle = (newTitle) => {
      if (!newTitle) {
        if (props.variantSpecArray?.length > 0) {
          const $productSwatches = productItem?.value?.querySelectorAll(".js-product-swatch") || [];

          if ($productSwatches?.length) {
            const $activeSwatch = [...$productSwatches].find(($swatch) => __.hasClass($swatch, "is-selected"));

            if ($activeSwatch) {
              const title = $activeSwatch?.dataset?.title || null;

              if (title) {
                subtitle.value = title;
              }
            }
          }
        }
        return;
      }
      const desc = productItem?.value?.querySelector(".js-pi-subtitle") || null;
      const height = desc && desc?.offsetHeight || 0;

      // pin height to avoid layout shift
      if (height > 0) {
        desc.style.minHeight = `${height}px`;
      }

      subtitle.value = newTitle;
    };

    const handleMouseEnter = () => {
      if (!isDesktop.value) return;
      if (props.variantSpecArray?.length) {
        const $productSwatches = productItem?.value?.querySelectorAll(".js-product-swatch") || [];

        if ($productSwatches?.length) {
          const $activeSwatch = [...$productSwatches].find(($swatch) => __.hasClass($swatch, "is-selected"));

          if ($activeSwatch) {
            const newImage = $activeSwatch?.dataset?.hover || null;

            if (newImage) {
              hoveredImage.value = newImage;

              // nextTick to ensure transition is applied
            nextTick(() => {
              isHovered.value = true;
            });

              return;
            }
          }
        }

        const firstVariant = props.variantSpecArray.find((variant) => variant?.hoverAsset);
        if (firstVariant && firstVariant?.json?.title && firstVariant?.hoverAsset) {

          hoveredImage.value = firstVariant.hoverAsset;
          // nextTick to ensure transition is applied
          nextTick(() => {
              isHovered.value = true;
            });
        }
      }
    };

    const onSwatchHover = (event) => {
      if (!isDesktop.value) return;
      if (props.variantSpecArray?.length > 0) {
        const hoverImage = event?.target?.dataset?.image || null;
        const title = event?.target?.dataset?.title || null;

        if (hoverImage) {
          hoveredImage.value = hoverImage;

          // nextTick to ensure transition is applied
          nextTick(() => {
              isHovered.value = true;
            });

          if (title) {
            updateTitle(title);
          }
        }
      }
    };

    const onImageHover = (event) => {
      if (!isDesktop.value) return;
      if (props.variantSpecArray?.length > 0) {
        const $item = event?.target?.closest('.js-product-item');
        if ($item) {
          const $activeSwatch = $item.querySelector('.js-product-swatch.is-selected');
          const hoverImage = $activeSwatch?.dataset?.image || null;
          const title = $activeSwatch?.dataset?.title || null;
          if (hoverImage && title) updateTitle(title);
        }
      }
    };

    const handleMouseLeave = () => {
      if (!isDesktop.value) return;
      isHovered.value = false;
      hoveredImage.value = null;
      updateTitle(props.productSubtitle);
    };

    const setActiveSwatches = () => {
      if (props.variantSpecArray?.length) {
        const $productSwatches = productItem?.value?.querySelectorAll(".js-product-swatch") || [];

        if ($productSwatches?.length) {
          const $activeSwatch = [...$productSwatches].find(($swatch) => __.hasClass($swatch, "is-selected"));

          if ($activeSwatch) {
            activeVariantTitle.value = $activeSwatch.dataset.title || null;
          }
        }
      }
    };

    // FrameSpec
    const {
      frameSpec,
      frameSpecUrl,
      isUploadProcessing,
      canPrintArtwork,
      checkFrameSpecData,
      clearFrameSpec,
      updateSpecOnSwatchClick,
      customerUploadActive,
      showMaskRenderer
    } = useFrameSpecData(
      productOrParentProduct.value,
      selectedVariantTitle,
      selectedVariantSpec,
      parentProductVariant?.value || activeVariantId.value,
      props.allFullMouldings
    );

    let pendingVisibilityObserver = null;

    const disconnectVisibilityObserver = () => {
      pendingVisibilityObserver?.disconnect();
      pendingVisibilityObserver = null;
    };

    // Defers the FrameSpec build until the card is near the viewport, so off-screen cards don't build/render eagerly.
    const buildFrameSpecWhenVisible = () => {
      const runBuild = () => {
        checkFrameSpecData(
          productOrParentProduct.value,
          selectedVariantTitle,
          selectedVariantSpec,
          parentProductVariant?.value || activeVariantId.value
        );
      };

      if (!("IntersectionObserver" in window) || !productItem.value) {
        runBuild();
        return;
      }

      disconnectVisibilityObserver();

      pendingVisibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            disconnectVisibilityObserver();
            runBuild();
          }
        });
      }, { rootMargin: "200px" });

      pendingVisibilityObserver.observe(productItem.value);
    };

    watch(
      [frameSpec, () => props.isCollectionPage],
      async ([newFrameSpec, isCollectionPage]) => {
        if (!isCollectionPage || !newFrameSpec?.product) {
          return;
        }

        if (!newFrameSpec.canPrintArtwork?.() || !newFrameSpec.needsCrop?.()) {
          return;
        }

        try {
            canPrintArtwork.value = newFrameSpec.canPrintArtworkInSelectedFrame();
        } catch (error) {
          console.error("Error during autoCrop on collection page:", error);
        }
      },
      { immediate: true }
    );

    // change slide on swipe
    const slide = (event, caro) => {
      const { deltaX } = event || 0;

      if (deltaX > 0) {
        caro.next();
      }

      if (deltaX < 0) {
        caro.previous();
      }
    };

    const initQuickview = () => {
      // ensure quickview is in DOM
      const $quickview = document.getElementById("quickview");

      if ($quickview) {
        const quickview = SDG?.quickview({
          type: "single",
          wrapper: productItem.value
        });

        // if quickview is not available
        if (!quickview) {return;}

        quickview?.init();
        const $parent = __.parents(productItem.value, ".js-pi") || null;
        const $links = productItem?.value?.querySelectorAll(".pi__link") || null;

        if ($parent?.length > 0) {
          $parent[0]?.classList?.add("js-quickview-button");

          if ($links?.length > 0) {
            // remove href
            [...$links].forEach(($link) => {
              $link.removeAttribute("href");
            });
          }

          // attach event
          productItem.value.addEventListener("click", (e) => {
            // exclude favoite
            if (e.target.closest(".js-product-heart")) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            // exclude swatches
            if (e.target.closest(".js-product-swatch")) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            quickview.open(e);
          });
        }
      }
    };

    const destroyQuickview = () => {
      const $parent = __.parents(productItem.value, ".js-pi") || null;
      const $links = productItem?.value?.querySelectorAll(".pi__link") || null;

      if ($parent?.length > 0) {
        $parent[0]?.classList?.remove("js-quickview-button");

        if ($links?.length > 0 && url?.value) {
          // when the link el is not an anchor
          [...$links].forEach(($link) => {
            if (!($link instanceof HTMLAnchorElement)) {
              const $newLink = document.createElement("a");
              $newLink.setAttribute("href", url.value);

              // insert new link
              $link.parentNode.insertBefore($newLink, $link);
              $newLink.appendChild($link);
              return;
            }

            $link.setAttribute("href", url.value);
          });
        }

        // renmove event
        productItem?.value?.removeEventListener("click", null);
      }
    };

    const productUrl = computed(() => {
      if (props.parentProduct?.handle && parentProductVariant.value) {
        return `/products/${props.parentProduct.handle}?variant=${parentProductVariant.value}`;
      }
      return url.value;
    });

    onMounted(() => {
      initializeCarousel();
      setActiveSwatches();

      if (frameSpec.value === null && !isBundle.value && window.collectionUploadEligible) {
        buildFrameSpecWhenVisible();
      }

      // event to handle swatch quicks in quickview drawer
      document.addEventListener(`updateSpecOnSwatchClick-${props.product.id}`, async (event) => {
        const { id, callback } = event.detail || {};

        await updateSpecOnSwatchClick(id);
        // small timeout for html to update
        setTimeout(() => {
          if (callback && typeof callback === "function") {
            callback();
          }
        }, 250);
      });

      // handle upload item
      window.addEventListener("uploadItem", (event) => {
        const { added, removed } = event.detail || {};

        if (added && !isBundle.value) {
          if (!window.collectionUploadEligible) {
            return;
          }

          buildFrameSpecWhenVisible();

          isQuickview.value = true;

          // small timeout for artwork to render
          setTimeout(() => {
            initQuickview();
          }, 500);
        }

        if (removed) {
          disconnectVisibilityObserver();
          isLoading.value = true;
          isQuickview.value = false;
          clearFrameSpec();
          destroyQuickview();

          // small timeout to smooth remove transition
          setTimeout(() => {
            if (!frameSpec.value) {
              isLoading.value = false;
            }
          }, 500);
        }
      });
    });

    // clean up
    onBeforeUnmount(() => {
      disconnectVisibilityObserver();
      document.removeEventListener(`updateSpecOnSwatchClick-${props.product.id}`, null);
      window.removeEventListener("uploadItem", null);
    });

    return {
      activeImage,
      frameSpec,
      canPrintArtwork,
      showMaskRenderer,
      customerUploadActive,
      errorUploadMessage,
      frameSpecUrl,
      isUploadProcessing,
      msrp,
      price,
      title,
      processedTitle,
      url,
      type,
      productImages,
      subtitle,
      badge,
      dimensions,
      valueProps,
      totalVariants,
      productItem,
      activeColor,
      initialColor,
      showSwatches,
      isActive,
      isGiftCardPage,
      noImage,
      src,
      alt,
      variants,
      hasVariedPrices,
      colors,
      formattedPrice,
      handleClick,
      resetActive,
      updateImage,
      slide,
      hasMaskedRendering,
      isBundle,
      isLoading,
      joineryHost: SDG?.Data?.joineryHost || "https://staging.framebridge.com",
      isCuratedDesignFrame,
      disableProductItemGalleryCarousel,
      isMutliPhotoFrame,
      isFrameBundle,
      isCustomFrame,
      handleMouseEnter,
      handleMouseLeave,
      onSwatchHover,
      onImageHover,
      isHovered,
      hoveredImage,
      activeVariantTitle,
      parentProductVariant,
      productUrl
    };
  }
};
</script>
